import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {ReactNode} from 'react';

import styles from './EnhancedTableCellNode.module.css';
import {TableCellComponent} from './TableCellComponent';

export type SerializedTableCellDecoratorNode = Spread<
  {
    type: 'TableCellDecorator';
    cellId: string;
    guidance: string;
  },
  SerializedLexicalNode
>;

function convertTableCellDecoratorElement(domNode: HTMLElement): DOMConversionOutput | null {
  const cellId = domNode.getAttribute('data-cell-id');
  if (cellId) {
    const node = $createTableCellDecoratorNode(cellId, '');
    return {node};
  }
  return null;
}

export class TableCellDecoratorNode extends DecoratorNode<ReactNode> {
  __cellId: string;
  __guidance: string;

  static getType(): string {
    return 'TableCellDecorator';
  }

  static clone(node: TableCellDecoratorNode): TableCellDecoratorNode {
    return new TableCellDecoratorNode(node.__cellId, node.__guidance, node.__key);
  }

  constructor(cellId: string, guidance: string, key?: NodeKey) {
    super(key);
    this.__cellId = cellId;
    this.__guidance = guidance;
  }

  static importJSON(json: SerializedTableCellDecoratorNode): TableCellDecoratorNode {
    return new TableCellDecoratorNode(json.cellId, json.guidance);
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (
          domNode.hasAttribute('data-lexical-table-cell') &&
          domNode.hasAttribute('data-cell-id')
        ) {
          return {
            conversion: convertTableCellDecoratorElement,
            priority: 1,
          };
        }
        return null;
      },
    };
  }

  createDOM(): HTMLElement {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-table-cell', 'true');
    element.setAttribute('data-cell-id', this.__cellId);
    element.classList.add(styles.tableCellDecorator);
    return element;
  }

  updateDOM(): boolean {
    return false;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    // Make this node non-navigable with keyboard
    return false;
  }

  isIsolated(): boolean {
    // Make this node behave as a single, indivisible unit
    // This prevents the cursor from entering the node during navigation
    return true;
  }

  getTextContent(): string {
    // Return empty string so it doesn't affect text content
    return '';
  }

  exportDOM(): DOMExportOutput {
    // Return null to exclude from copy/paste operations
    return {element: null};
  }

  exportJSON(): SerializedTableCellDecoratorNode {
    return {
      type: 'TableCellDecorator',
      cellId: this.__cellId,
      guidance: this.__guidance,
      version: 1,
    };
  }

  getCellId(): string {
    return this.__cellId;
  }

  getGuidance(): string {
    return this.__guidance;
  }

  setGuidance(guidance: string): void {
    const writableNode = this.getWritable();
    writableNode.__guidance = guidance;
  }

  decorate(): ReactNode {
    return <TableCellComponent cellId={this.__cellId} guidance={this.__guidance} />;
  }
}

export function $createTableCellDecoratorNode(
  cellId: string,
  guidance: string
): TableCellDecoratorNode {
  return $applyNodeReplacement(new TableCellDecoratorNode(cellId, guidance));
}

export function $isTableCellDecoratorNode(node: unknown): node is TableCellDecoratorNode {
  return node instanceof TableCellDecoratorNode;
}
